/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-report core: report-card inclusion lifecycle (no DOM).
// Source: cardiac_report_v0_5_8.html at cardiac-panel-v4 git 12d10d7 —
// markReportEvidenceState, hasAcceptedThirdPartyEvidence,
// unresolvedStateForFeature, setFeatureReportInclusion,
// undoFeatureReportInclusion, featureHasCommittedAssessment, beginFeatureEdit,
// featureEditHasChanges, saveFeatureToReport, restoreFeatureAssessment,
// cancelFeatureEdits, reconcileReportInclusionStates,
// outstandingReportFeatures and the rewritten reportFeatures.
//
// Every standard card carries a `reportInclusionStatus`:
// - "accepted"   the card is in the report. Evidence accepted in the viewer
//                arrives in this state; a card the clinician assessed by hand
//                gets here through Save.
// - "unresolved" no accepted automated evidence stands behind the card. It
//                shows in clinical review only, starting from a neutral marker
//                that is not a result, until the clinician saves an assessment
//                or excludes the card.
// - "pending"    evidence exists but the card has not been included. Either
//                the accepted Timing recordings disagree on the heart rate, so
//                the four timing cards arrive showing the complete accepted
//                range and wait for the reviewer to choose which recordings
//                each card includes, adjust the value if needed, and Save the
//                card — or Exclude it — or change the acceptances in the
//                review (the Heart Rate card's `candidateEvidenceMeta` carries
//                the roll-up's account of the disagreement); or nothing was
//                accepted for the card's domain but a recording accepted for
//                the other domain has usable evidence for it, which arrives
//                as a candidate (`candidateEvidenceMeta.candidate`) to review
//                and Save, edit, or Exclude. A pending card's marker is drawn
//                dotted until the reviewer acts on it.
// - "excluded"   the clinician left the card out; reversible until approval.
//                A SYS:DIA Ratio or SYS:DIA Duration card with no measurement
//                behind it is excluded on import instead
//                (`measurementUnavailable`, origin "measurement_unavailable"):
//                these two cards state a calculated measurement, so there is
//                nothing to assess by hand, and the card cannot be restored
//                until evidence supplies the measurement.
// Patient and provider views, the delivered documents and approval all work
// from the accepted cards only. Approval additionally requires that no card
// is still unresolved or pending.
//
// Unlocking the report opens an edit session on every accepted card
// (`beginAcceptedFeatureEdits`): cards accepted in the review arrive in the
// report without a duplicate approval step and are simply editable. Save is
// available throughout the session; saving an unchanged card only closes it.
//
// Deviations: `data.features` global -> explicit `features` argument; Date ->
// `now` option. The prototype keeps a card's edit session (`cardEditMode`,
// `editSessionBaseline`) on the feature and strips it when it serializes;
// here the whole feature object is the saved document, so the session lives
// in a WeakMap beside it and never reaches a saved version.

import {
  buildFeatureBaseline,
  findFeatureByName,
  isHeartRateFeature,
  resetFeatureSourcesToBaseline,
  restoreTimingSelection,
  sourceIdForFeed,
} from "./features"
import { restoreStructuralSources } from "./structural-sources"

export const INCLUSION_ACCEPTED = "accepted"
export const INCLUSION_UNRESOLVED = "unresolved"
export const INCLUSION_PENDING = "pending"
export const INCLUSION_EXCLUDED = "excluded"

export const TIMING_DOMAIN_CARDS = ["Heart Rate", "SYS:DIA Ratio", "SYS:DIA Duration", "Cycle Regularity"]

export const MEASUREMENT_UNAVAILABLE_ORIGIN = "measurement_unavailable"
export const MEASUREMENT_UNAVAILABLE_REASON =
  "No accepted analysis source provided sufficient data to calculate this result. This card is excluded from the report."
export const MEASUREMENT_UNAVAILABLE_PATIENT_TEXT =
  "No calculated result is available, so this card is not included in the report."
export const UNRESOLVED_PATIENT_TEXT =
  "No automated result is available. The reviewing clinician may assess this card manually."
export const UNRESOLVED_SUGGESTED_INTERPRETATION =
  "No automated result is available. Assess independently and set this card manually."

export const MANUAL_SAVE_HINT = "Set the Observed Value and/or assessment marker before saving this card to the report."
export const TIMING_REVIEW_TITLE = "Review required"
export const HEART_RATE_REVIEW_DETAIL =
  "Heart rate varied between accepted recordings. Select the recordings to include, edit the Observed Value if needed, then Save—or Exclude this card."
export const TIMING_REVIEW_DETAIL = "Review the included recording values below, then Save—or Exclude this card."
export const REVIEW_INCOMPLETE_MESSAGE = "Review incomplete — some cards still require Save or Exclude."
export const NO_INCLUDED_CARDS_MESSAGE = "Save at least one card before approving the report."

const NOT_TRACKED = new Set(["Timing Summary", "Structural Sound Detail", "S1/S2 Intensity"])

// Cards that take part in the lifecycle: everything a report view can show.
export function isInclusionTrackedFeature(feature) {
  if (!feature || feature.type === "signal" || feature.name === "Signal Quality") return false
  return !NOT_TRACKED.has(feature.name)
}

// Upstream lets an accepted third-party source include a card on its own. On
// this platform ACT and BeatAware results are information for the reviewer:
// only the PCG viewer's accepted evidence or the clinician's own assessment
// puts a card in the report, so this is always false. Kept, with its callers,
// so the vendored inclusion rules read as upstream's.
export function hasAcceptedThirdPartyEvidence(_feature) {
  return false
}

// A card's roll-up evidence names the recordings it came from; a domain
// nobody accepted names none.
const hasAcceptedViewerEvidence = (feature) =>
  (Array.isArray(feature.sourceRecordingIds) && feature.sourceRecordingIds.length > 0) ||
  (feature.sources || []).some((source) => sourceIdForFeed(source) === "pcg_viewer" && source.recordingId)

// The state of a card that has none recorded.
export function unresolvedStateForFeature(feature) {
  if (feature && feature.candidateEvidenceMeta) return INCLUSION_PENDING
  if (hasAcceptedThirdPartyEvidence(feature)) return INCLUSION_ACCEPTED
  return INCLUSION_UNRESOLVED
}

export function inclusionStateOf(feature) {
  return (feature && feature.reportInclusionStatus) || unresolvedStateForFeature(feature)
}

// Import-time state: what the evidence behind the card says, before any
// clinician decision. No review time is recorded here (the prototype stamps
// the import time): nobody has reviewed the card in the report yet, and when
// the evidence was accepted is on the recording's disposition.
export function markReportEvidenceState(feature, state, candidateMeta = null) {
  if (!feature) return
  feature.reportInclusionStatus = state
  feature.reportInclusionReviewedAt = null
  feature.reportInclusionPreviousStatus = null
  feature.reportInclusionOrigin = null
  feature.candidateEvidenceMeta = candidateMeta
  feature.manualAssessmentStarted = false
  feature.savedReportBaseline = null
  feature.approved = false
}

// Gives every tracked card a state. Documents saved before cards carried one
// are read by their evidence: the timing cards were in the report when the
// heart-rate summary names source recordings, any other card when its own
// evidence does, and a card the clinician already worked on always was. The
// rest had nothing accepted behind them.
export function reconcileReportInclusionStates(features) {
  const timingAccepted = hasAcceptedViewerEvidence(findFeatureByName(features, "Heart Rate") || {})
  ;(features || []).forEach((feature) => {
    if (!isInclusionTrackedFeature(feature)) return
    if (!feature.reportInclusionStatus) {
      const evidenced = TIMING_DOMAIN_CARDS.includes(feature.name) ? timingAccepted : hasAcceptedViewerEvidence(feature)
      feature.reportInclusionStatus =
        evidenced || feature.reviewTouched ? INCLUSION_ACCEPTED : unresolvedStateForFeature(feature)
    }
    if (hasAcceptedThirdPartyEvidence(feature) && feature.reportInclusionStatus === INCLUSION_UNRESOLVED) {
      feature.reportInclusionStatus = INCLUSION_ACCEPTED
      feature.reportInclusionOrigin = "third_party_evidence"
    }
  })
}

// The cards in the report, whatever view is showing.
export function includedReportFeatures(features) {
  return (features || []).filter(
    (feature) =>
      isInclusionTrackedFeature(feature) && !feature.isHidden && feature.reportInclusionStatus === INCLUSION_ACCEPTED
  )
}

// Cards the clinician still has to save or exclude.
export function outstandingReportFeatures(features) {
  return (features || []).filter(
    (feature) =>
      isInclusionTrackedFeature(feature) &&
      !feature.isHidden &&
      [INCLUSION_PENDING, INCLUSION_UNRESOLVED].includes(inclusionStateOf(feature))
  )
}

// Why the report cannot be approved yet, or null when it can.
export function approvalBlockReason(features) {
  if (outstandingReportFeatures(features).length) return REVIEW_INCOMPLETE_MESSAGE
  if (!includedReportFeatures(features).length) return NO_INCLUDED_CARDS_MESSAGE
  return null
}

const originFor = (feature) =>
  feature.manualAssessmentStarted || feature.noteToDoctorEdited || feature.reviewTouched
    ? "clinician_modified"
    : hasAcceptedThirdPartyEvidence(feature)
    ? "third_party_evidence"
    : "hcp_report_review"

// ---- Card edit session (never persisted) --------------------------------

const editSessions = new WeakMap()

export function isCardEditing(feature) {
  return editSessions.has(feature)
}

// A saved card is read-only until Edit; every other card is always editable,
// except one left out because it has no measurement to state.
export function isCardEditable(feature) {
  if (feature?.measurementUnavailable) return false
  return inclusionStateOf(feature) !== INCLUSION_ACCEPTED || isCardEditing(feature)
}

// The neutral starting state of a card with nothing behind it: the marker is
// orientation only and the observed value reads "To be manually set".
export function isAssessmentUnset(feature) {
  return inclusionStateOf(feature) === INCLUSION_UNRESOLVED && !feature.manualAssessmentStarted
}

// A pending card the reviewer has not acted on yet: its value is derived
// evidence awaiting Save, an edit, or Exclude, and its marker is drawn dotted.
export function isCandidateAwaitingReview(feature) {
  return inclusionStateOf(feature) === INCLUSION_PENDING && !feature.manualAssessmentStarted
}

const sessionBaseline = (feature) => ({
  ...buildFeatureBaseline(feature),
  noteToDoctorEdited: !!feature.noteToDoctorEdited,
  noteToDoctorStale: !!feature.noteToDoctorStale,
})

// What Cancel puts back besides the assessment itself, so that a cancelled
// edit leaves no trace of a clinician assessment on the card
const SESSION_FLAGS = ["manualAssessmentStarted", "reviewTouched", "reportInclusionOrigin"]

export function beginFeatureEdit(features, index) {
  const feature = features[index]
  if (!feature || feature.reportInclusionStatus !== INCLUSION_ACCEPTED) return false
  editSessions.set(feature, {
    baseline: sessionBaseline(feature),
    flags: Object.fromEntries(SESSION_FLAGS.map((key) => [key, feature[key]])),
  })
  return true
}

/**
 * Unlocking the report: every accepted card that shows in clinical review
 * opens its edit session, so it is editable without a per-card Edit step.
 * Returns how many cards were opened.
 */
export function beginAcceptedFeatureEdits(features) {
  let opened = 0
  ;(features || []).forEach((feature, index) => {
    if (feature && !feature.isHidden && beginFeatureEdit(features, index)) opened += 1
  })
  return opened
}

export function featureEditHasChanges(feature) {
  const session = editSessions.get(feature)
  if (!session) return false
  return JSON.stringify(sessionBaseline(feature)) !== JSON.stringify(session.baseline)
}

// A card can be saved once it says something: accepted or pending evidence,
// accepted third-party evidence, or an assessment the clinician started.
export function featureHasCommittedAssessment(feature) {
  if (!feature) return false
  if (feature.reportInclusionStatus === INCLUSION_ACCEPTED) return true
  if (feature.reportInclusionStatus === INCLUSION_PENDING || feature.candidateEvidenceMeta) return true
  if (hasAcceptedThirdPartyEvidence(feature)) return true
  return !!feature.manualAssessmentStarted
}

// Whether the card's Save button is live: something to save, and — on a
// card already in the report — an open edit session.
export function canSaveFeatureToReport(feature) {
  if (!featureHasCommittedAssessment(feature)) return false
  return inclusionStateOf(feature) !== INCLUSION_ACCEPTED || isCardEditing(feature)
}

export function saveFeatureToReport(features, index, { now = () => new Date().toISOString() } = {}) {
  const feature = features[index]
  if (!feature) return { ok: false, reason: null }
  if (!featureHasCommittedAssessment(feature)) return { ok: false, reason: MANUAL_SAVE_HINT }
  // Saving a card already in the report without a change only closes its session
  if (inclusionStateOf(feature) === INCLUSION_ACCEPTED && isCardEditing(feature) && !featureEditHasChanges(feature)) {
    editSessions.delete(feature)
    return { ok: true, reason: null }
  }
  feature.reportInclusionPreviousStatus = inclusionStateOf(feature)
  feature.reportInclusionStatus = INCLUSION_ACCEPTED
  feature.reportInclusionReviewedAt = now()
  feature.reportInclusionOrigin = feature.manualAssessmentStarted
    ? "clinician_modified"
    : hasAcceptedThirdPartyEvidence(feature)
    ? "third_party_evidence"
    : "hcp_report_review"
  feature.savedReportBaseline = sessionBaseline(feature)
  editSessions.delete(feature)
  feature.reviewTouched = true
  return { ok: true, reason: null }
}

function restoreFeatureAssessment(feature, baseline) {
  if (!feature || !baseline) return
  feature.observed = baseline.observed
  feature.status = baseline.status
  feature.aiStatus = baseline.aiStatus || feature.aiStatus
  feature.markerPosition = baseline.markerPosition
  feature.clinicianAssessmentMode = baseline.clinicianAssessmentMode || null
  feature.clinicianRangeBpm = baseline.clinicianRangeBpm ? { ...baseline.clinicianRangeBpm } : null
  feature.sourceRangeBpm = baseline.sourceRangeBpm ? { ...baseline.sourceRangeBpm } : feature.sourceRangeBpm || null
  feature.mixedRange = !!baseline.mixedRange
  restoreTimingSelection(feature, baseline)
  restoreStructuralSources(feature, baseline)
  feature.noteToPatient = baseline.noteToPatient || ""
  feature.patientExplanationText = baseline.patientExplanationText || feature.patientExplanationText || ""
  feature.patientResultText = baseline.patientResultText || feature.patientResultText || ""
  feature.noteToDoctor = baseline.noteToDoctor || ""
  feature.noteToDoctorEdited = !!baseline.noteToDoctorEdited
  feature.noteToDoctorStale = !!baseline.noteToDoctorStale
  resetFeatureSourcesToBaseline(feature, baseline)
}

// Cancel: back to where the edit session started (a saved card), or to the
// card's untouched state (a card that was never saved).
export function cancelFeatureEdits(features, index) {
  const feature = features[index]
  if (!feature) return false
  const wasAccepted = feature.reportInclusionStatus === INCLUSION_ACCEPTED
  const session = editSessions.get(feature)
  restoreFeatureAssessment(
    feature,
    (session && session.baseline) || feature.savedReportBaseline || feature.importedBaseline
  )
  if (session) Object.assign(feature, session.flags)
  editSessions.delete(feature)
  if (!wasAccepted) {
    feature.manualAssessmentStarted = false
    feature.reviewTouched = false
    feature.reportInclusionStatus = feature.reportInclusionPreviousStatus || unresolvedStateForFeature(feature)
  }
  return true
}

export function setFeatureReportInclusion(
  features,
  index,
  state,
  { origin, now = () => new Date().toISOString() } = {}
) {
  const feature = features[index]
  if (!feature || ![INCLUSION_ACCEPTED, INCLUSION_EXCLUDED].includes(state)) return false
  feature.reportInclusionPreviousStatus = inclusionStateOf(feature)
  feature.reportInclusionStatus = state
  feature.reportInclusionReviewedAt = now()
  feature.reportInclusionOrigin = origin || originFor(feature)
  feature.reviewTouched = true
  if (state === INCLUSION_EXCLUDED) feature.approved = false
  editSessions.delete(feature)
  return true
}

// "Restore for review": back to the state the card had before it was excluded.
export function undoFeatureReportInclusion(features, index) {
  const feature = features[index]
  // A card without a measurement stays out until evidence supplies one
  if (!feature || feature.measurementUnavailable) return false
  feature.reportInclusionStatus = feature.reportInclusionPreviousStatus || unresolvedStateForFeature(feature)
  feature.reportInclusionPreviousStatus = null
  feature.reportInclusionReviewedAt = null
  feature.reportInclusionOrigin = null
  feature.approved = false
  return true
}

// A change the clinician makes to a card already in the report.
export function noteClinicianModification(feature) {
  if (feature && feature.reportInclusionStatus === INCLUSION_ACCEPTED) {
    feature.reportInclusionOrigin = "clinician_modified"
  }
}

// What a pending or unassessed card says about itself: a timing disagreement
// is put as what to do, a card with nothing behind it as a manual
// assessment, and candidate evidence by where it comes from.
function reviewStateWording(feature, state, meta) {
  if (state === INCLUSION_PENDING && meta.timingRange) {
    return {
      title: TIMING_REVIEW_TITLE,
      detail: isHeartRateFeature(feature) ? HEART_RATE_REVIEW_DETAIL : TIMING_REVIEW_DETAIL,
    }
  }
  const title = "Review required · not yet included"
  if (state === INCLUSION_UNRESOLVED) {
    return {
      title: "No accepted automated evidence · HCP manual acceptance required",
      detail:
        "Listen and assess independently, then set the Observed Value and/or marker. Automated cues are optional guidance.",
    }
  }
  const sourceCount = Array.isArray(meta.sourceRecordingIds) ? meta.sourceRecordingIds.length : 0
  if (meta.candidate && sourceCount) {
    // Upstream words its candidates as "five-cycle-reviewed" recordings;
    // here they are the recordings the review accepted for the other domain
    const recordings = `${sourceCount} recording${sourceCount === 1 ? "" : "s"}`
    const domain = meta.domain === "structuralSound" ? "Structural Sound" : "Timing"
    return { title, detail: `Candidate evidence from ${recordings} not accepted for ${domain} in the review.` }
  }
  return { title, detail: "One or more evidence sources are available but the final card has not been included." }
}

// What a clinical-review card says about its state: nothing on a card in
// the report; a bar on an excluded card; plain text on a card that still
// needs the clinician. A timing disagreement is put as what to do — choose
// the recordings, then Save or Exclude — and the roll-up's account of it is
// not repeated there.
export function inclusionStateSummary(feature) {
  const state = inclusionStateOf(feature)
  if (state === INCLUSION_ACCEPTED) return null
  if (state === INCLUSION_EXCLUDED && feature.measurementUnavailable) {
    return {
      state,
      tone: "review",
      title: "Not included — measurement unavailable",
      detail: feature.measurementUnavailableReason || MEASUREMENT_UNAVAILABLE_REASON,
      warning: null,
    }
  }
  if (state === INCLUSION_EXCLUDED) {
    return {
      state,
      tone: "excluded",
      title: "Excluded from report",
      detail: "The card remains visible here and can be restored before final approval.",
      warning: null,
    }
  }
  const meta = feature.candidateEvidenceMeta || {}
  const timingDiscrepancy = state === INCLUSION_PENDING && !!meta.timingRange
  const { title, detail } = reviewStateWording(feature, state, meta)
  return {
    state,
    tone: "review",
    title,
    detail,
    warning: !timingDiscrepancy && meta.reviewReason ? meta.reviewReason : null,
  }
}
